import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, PlusCircle, Sparkles, X } from "lucide-react";
import { api, type AddKeywordInput, type ClusterView, type Language, type Priority } from "../../lib/api.js";
import { NewClusterDialog } from "./NewClusterDialog.js";

/** Sentinel value for the "+ Create new cluster…" option in the cluster select. */
const NEW_CLUSTER = "__new__";

/**
 * Manual "Add keyword" dialog — operator types one keyword directly into the
 * pipeline. The keyword lands in status=backlog (regardless of whether enrichment
 * runs immediately) so it shows up in the table right away.
 */
export function AddKeywordDialog({
  defaultClusterId,
  onCancel,
  onCreated,
}: {
  defaultClusterId?: string | null;
  onCancel: () => void;
  onCreated: (keywordId: string) => void;
}) {
  const [draft, setDraft] = useState<AddKeywordInput>({
    keyword: "",
    language: "he",
    clusterId: defaultClusterId ?? null,
    priority: "medium",
    pillarPage: false,
    intent: "informational",
  });
  const [error, setError] = useState<string | null>(null);
  const [newClusterOpen, setNewClusterOpen] = useState(false);
  const qc = useQueryClient();

  const clustersQuery = useQuery({
    queryKey: ["pipeline", "clusters"],
    queryFn: () => api.listClusters(),
  });

  const create = useMutation({
    mutationFn: () => api.addKeyword(draft),
    onSuccess: (r) => onCreated(r.id),
    onError: (e: Error) => setError(e.message),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!draft.keyword.trim() || draft.keyword.trim().length < 2) {
      setError("Keyword must be at least 2 characters.");
      return;
    }
    create.mutate();
  }

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-ink/30 backdrop-blur-sm animate-fade-in" onClick={onCancel} />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
        <form
          onSubmit={handleSubmit}
          className="pointer-events-auto w-full max-w-lg space-y-4 rounded-2xl bg-white p-6 shadow-rail animate-fade-in"
        >
          <header>
            <div className="flex items-center gap-2 text-2xs uppercase tracking-wider text-brand-700">
              <PlusCircle className="h-3.5 w-3.5" />
              Add keyword
            </div>
            <h3 className="mt-1 text-lg font-semibold text-ink">Manual entry into the pipeline</h3>
            <p className="mt-1 text-xs text-muted text-balance">
              The keyword lands in <code className="font-mono text-2xs">backlog</code>. Use Enrich on the detail panel after to pull SerpAPI + competitor data.
            </p>
          </header>

          <div className="space-y-3">
            <div>
              <label className="label">Keyword <span className="text-danger">*</span></label>
              <input
                type="text"
                value={draft.keyword}
                onChange={(e) => setDraft({ ...draft, keyword: e.target.value })}
                placeholder="e.g. רפלקסולוגיה לזירוז לידה"
                className="input"
                dir={draft.language === "he" ? "rtl" : "ltr"}
                autoFocus
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Language</label>
                <select
                  value={draft.language}
                  onChange={(e) => setDraft({ ...draft, language: e.target.value as Language })}
                  className="input"
                >
                  <option value="he">Hebrew</option>
                  <option value="en">English</option>
                </select>
              </div>
              <div>
                <label className="label">Cluster</label>
                <select
                  value={draft.clusterId ?? ""}
                  onChange={(e) => {
                    if (e.target.value === NEW_CLUSTER) { setNewClusterOpen(true); return; }
                    setDraft({ ...draft, clusterId: e.target.value || null });
                  }}
                  className="input"
                >
                  <option value="">— Unassigned —</option>
                  {(clustersQuery.data?.clusters ?? []).map((c: ClusterView) => (
                    <option key={c.id} value={c.id}>
                      {c.nameEn}
                    </option>
                  ))}
                  <option value={NEW_CLUSTER}>+ Create new cluster…</option>
                </select>
              </div>
              <div>
                <label className="label">Priority</label>
                <select
                  value={draft.priority ?? "medium"}
                  onChange={(e) => setDraft({ ...draft, priority: e.target.value as Priority })}
                  className="input"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <label className="label">Intent</label>
                <select
                  value={draft.intent ?? "informational"}
                  onChange={(e) => setDraft({ ...draft, intent: e.target.value as AddKeywordInput["intent"] })}
                  className="input"
                >
                  <option value="informational">Informational</option>
                  <option value="commercial">Commercial</option>
                  <option value="transactional">Transactional</option>
                  <option value="navigational">Navigational</option>
                </select>
              </div>
            </div>

            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={draft.pillarPage ?? false}
                onChange={(e) => setDraft({ ...draft, pillarPage: e.target.checked })}
              />
              <span>Treat as a pillar page (3,000+ words, hub for the cluster)</span>
            </label>

            <div>
              <label className="label">Notes (optional)</label>
              <textarea
                value={draft.notes ?? ""}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value || null })}
                rows={2}
                placeholder="Editorial angle, why we're chasing this, anything the brief should reflect"
                className="textarea"
              />
            </div>
          </div>

          {error && (
            <div className="rounded-lg bg-danger-soft px-3 py-2 text-xs text-red-700 flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <footer className="flex items-center justify-end gap-2">
            <button type="button" onClick={onCancel} className="btn-secondary btn-sm" disabled={create.isPending}>
              <X className="h-3.5 w-3.5" />
              Cancel
            </button>
            <button type="submit" className="btn-primary btn-sm" disabled={create.isPending}>
              <Sparkles className="h-3.5 w-3.5" />
              {create.isPending ? "Adding…" : "Add to pipeline"}
            </button>
          </footer>
        </form>
      </div>

      {newClusterOpen && (
        <NewClusterDialog
          seedKeyword={draft.language === "he" ? draft.keyword.trim() || undefined : undefined}
          onCancel={() => setNewClusterOpen(false)}
          onCreated={(clusterId) => {
            setNewClusterOpen(false);
            qc.invalidateQueries({ queryKey: ["pipeline", "clusters"] });
            setDraft((d) => ({ ...d, clusterId }));
          }}
        />
      )}
    </>
  );
}
